import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ApmJoinModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact) {
      toast.error("Podaj imię i kontakt.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/apm/leads`, form);
      setDone(true);
      toast.success("Zgłoszenie wysłane. Odezwiemy się dyskretnie.");
      setForm({ name: "", contact: "", message: "" });
    } catch (err) {
      toast.error("Coś poszło nie tak. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-lg bg-white/[0.04] border border-white/12 text-white placeholder:text-white/35 focus:border-[#a78bfa] focus:outline-none focus:ring-2 focus:ring-[#8b5cf6]/30 transition-all";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="apm-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md rounded-2xl border border-[#8b5cf6]/30 bg-[#141225] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          >
            <button onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-white" data-testid="apm-modal-close">
              <X size={20} />
            </button>

            {done ? (
              <div className="text-center py-8" data-testid="apm-success">
                <CheckCircle2 className="mx-auto text-[#a78bfa] mb-4" size={48} strokeWidth={1.4} />
                <h3 className="font-serif-apm text-3xl text-white mb-2">Dziękujemy</h3>
                <p className="text-white/60">Twoje zgłoszenie trafiło do nas. Odezwiemy się dyskretnie.</p>
                <button onClick={onClose} className="mt-6 text-sm text-[#a78bfa] hover:underline">Zamknij</button>
              </div>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#a78bfa] mb-2">Zgłoszenie do APM</p>
                <h3 className="font-serif-apm text-3xl text-white mb-6 leading-tight">Zgłoś chęć dołączenia</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="apm-form">
                  <input data-testid="apm-name" className={inputCls} placeholder="Imię i nazwisko" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="apm-contact" className={inputCls} placeholder="E-mail lub telefon" value={form.contact} onChange={(e) => set("contact", e.target.value)} />
                  <textarea data-testid="apm-message" rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Kilka słów o sobie (opcjonalnie)" value={form.message} onChange={(e) => set("message", e.target.value)} />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="apm-submit"
                    className="w-full h-[52px] rounded-lg bg-[#8b5cf6] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#9d74f8] transition-colors disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Wysyłanie...</> : <>Wyślij zgłoszenie <Send size={16} /></>}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
